<template>
   <b-container fluid>
      <b-row>
         <b-col class="d-flex justify-content-between align-items-center">
            <h1 class="mb-3">Employees</h1>
            <div>
               <StudyPeriodSelect
                  ident="main"
                  v-model="periodId"
                  :active-study="!!activeStudyId"
                  @input="loadEmployees"
                  v-show="periodId"
               />
            </div>
         </b-col>
      </b-row>

      <b-alert :show="!periodId" variant="primary" class="standard-width mx-auto">
         <h6 class="mb-3">Select a study period to get started.</h6>
         <StudyPeriodSelect
            ident="initial"
            v-model="periodId"
            :active-study="!!activeStudyId"
            emptyText="Select a study period"
         />
      </b-alert>

      <b-row v-if="periodId">
         <b-col cols="4">
            <b-card>
               <div class="d-flex align-items-center justify-content-between">
                  <h2 class="mb-3">New Employee</h2>
                  <b-button
                     id="btn-import-employees"
                     size="sm"
                     variant="primary"
                     @click="startEmployeeImport"
                  >
                     Import
                  </b-button>
               </div>
               <b-form @submit.prevent="submitNewEmployee">
                  <b-form-group
                     label="Full Name"
                     label-for="input-new-full-name"
                     invalid-feedback="Required"
                  >
                     <b-form-input
                        id="input-new-full-name"
                        v-model="newEmployee.fullname"
                        :state="
                           $v.newEmployee.fullname.$invalid
                              ? !$v.newEmployee.fullname.$invalid
                              : null
                        "
                        ref="newEmployeeName"
                     ></b-form-input>
                  </b-form-group>

                  <b-form-group
                     label="Location"
                     label-for="input-new-location"
                     invalid-feedback="Required"
                  >
                     <b-form-select
                        :id="'input-new-location'"
                        :options="stateOptions"
                        v-model="newEmployee.location"
                        :state="
                           $v.newEmployee.location.$invalid
                              ? !$v.newEmployee.location.$invalid
                              : null
                        "
                     >
                        <template #first>
                           <b-form-select-option :value="null" disabled>
                              Please select one
                           </b-form-select-option>
                        </template>
                     </b-form-select>
                  </b-form-group>

                  <b-form-group
                     label="Employee ID"
                     label-for="input-new-employee-id"
                     invalid-feedback="Required"
                  >
                     <b-form-input
                        id="input-new-employee-id"
                        v-model="newEmployee.ident"
                     ></b-form-input>
                  </b-form-group>

                  <b-form-group label="Job Title" label-for="input-new-job-title">
                     <b-form-input
                        id="input-new-job-title"
                        v-model="newEmployee.title"
                        list="datalist-titles"
                        autocomplete="off"
                     ></b-form-input>
                  </b-form-group>

                  <b-form-group label="Department" label-for="input-new-department">
                     <b-form-input
                        id="input-new-department"
                        v-model="newEmployee.department"
                        list="datalist-departments"
                        autocomplete="off"
                     ></b-form-input>
                  </b-form-group>

                  <b-form-group label="Company Officer" label-for="select-new-officer">
                     <template #label
                        >Company Officer
                        <b-icon
                           icon="question-circle"
                           v-b-tooltip.bottom="
                              'For (S/C)Corp, officers are identified by Form 1125-E. For partnerships and LLCs, officers are any members with officer-like authority.'
                           "
                        ></b-icon
                     ></template>
                     <b-form-select
                        id="select-new-officer"
                        :options="[
                           {value: true, text: 'Yes'},
                           {value: false, text: 'No'},
                        ]"
                        v-model="newEmployee.officer"
                     >
                     </b-form-select>
                  </b-form-group>

                  <b-form-group label="Subsidiary Company Name" label-for="input-new-subsidiary">
                     <b-form-input
                        id="input-new-subsidiary"
                        v-model="newEmployee.subsidiary"
                        list="datalist-subsidiaries"
                        autocomplete="off"
                     ></b-form-input>
                  </b-form-group>

                  <label v-if="isRndig" for="payroll" class="form-label fw-bold"
                     >Employee Taxable Wages</label
                  >
                  <div v-if="isRndig" class="currency-input-wrapper">
                     <span class="currency-symbol">$</span>
                     <b-form-input
                        id="payroll"
                        v-model="payroll"
                        type="text"
                        @blur="formatCurrency"
                        @focus="unformatCurrency"
                        class="currency-input"
                        @keypress="onlyNumbers"
                     />
                  </div>

                  <b-form-row>
                     <b-col class="d-flex justify-content-end mt-4">
                        <b-button
                           id="btn-add-employee"
                           class="d-flex align-items-center"
                           variant="primary"
                           type="submit"
                           :disabled="$v.newEmployee.$invalid || newEmployee.saving"
                        >
                           <b-spinner small v-if="newEmployee.saving" class="mr-1"></b-spinner>
                           Add
                        </b-button>
                     </b-col>
                  </b-form-row>
               </b-form>
            </b-card>
         </b-col>
         <b-col cols="8">
            <b-card>
               <div v-if="isRndig" class="d-flex align-items-center justify-content-between mb-3">
                  <button
                     class="icon-btn icon-btn-secondary"
                     @click="selectAll(!anyEmployeesSelected)"
                  >
                     <b-icon :icon="anyEmployeesSelected ? 'dash-square' : 'plus-square'" />
                  </button>
                  <b-button
                     id="btn-delete-selected"
                     variant="danger"
                     :disabled="!anyEmployeesSelected"
                     @click="deleteEmployeesModal"
                  >
                     Delete Selected
                  </b-button>
               </div>

               <b-table
                  id="table-edit-employees"
                  class="mb-0 scrollbar"
                  :fields="fields"
                  sort-by="fullname"
                  :items="employees"
                  responsive
                  sticky-header="577px"
               >
                  <template #cell(fullname)="data">
                     <div class="d-flex align-items-center">
                        <b-form-checkbox
                           v-if="isRndig"
                           v-model="selection[data.item.id]"
                           :value="true"
                           size="lg"
                        ></b-form-checkbox>
                        <button
                           :id="`btn-edit-employee-${data.index + 1}`"
                           class="icon-btn icon-btn-secondary mr-3"
                           @click="editEmployeeModal(data.item)"
                        >
                           <b-icon-pencil
                              style="width: 1.2rem; height: 1.2rem"
                              icon="archive"
                              aria-hidden="true"
                           ></b-icon-pencil>
                        </button>
                        {{ data.value }}
                     </div>
                  </template>
               </b-table>
            </b-card>
         </b-col>
      </b-row>

      <b-modal
         id="modal-edit-employee"
         title="Edit Employee"
         @ok="submitEditEmployee"
         :ok-disabled="$v.editEmployee.$invalid"
         ok-title="Submit"
      >
         <b-form @submit.prevent="submitEditEmployee">
            <b-form-group
               label="Full Name"
               label-for="input-edit-full-name"
               invalid-feedback="Required"
            >
               <b-form-input
                  id="input-edit-full-name"
                  v-model="editEmployee.fullname"
                  :state="!$v.editEmployee.fullname.$invalid"
               ></b-form-input>
            </b-form-group>

            <b-form-group
               label="Location"
               label-for="input-edit-location"
               invalid-feedback="Required"
            >
               <b-form-select
                  :id="'input-edit-location'"
                  :options="stateOptions"
                  v-model="editEmployee.location"
                  :state="!$v.editEmployee.location.$invalid"
               >
                  <template #first>
                     <b-form-select-option :value="null" disabled>
                        Please select one
                     </b-form-select-option>
                  </template>
               </b-form-select>
            </b-form-group>

            <b-form-group
               label="Employee ID"
               label-for="input-edit-employee-id"
               invalid-feedback="Required"
            >
               <b-form-input
                  id="input-edit-employee-id"
                  v-model="editEmployee.ident"
               ></b-form-input>
            </b-form-group>

            <b-form-group label="Job Title" label-for="input-edit-job-title">
               <b-form-input
                  id="input-edit-job-title"
                  v-model="editEmployee.title"
                  list="datalist-titles"
                  autocomplete="off"
               ></b-form-input>
            </b-form-group>

            <b-form-group label="Department" label-for="input-edit-department">
               <b-form-input
                  id="input-edit-department"
                  v-model="editEmployee.department"
                  list="datalist-departments"
                  autocomplete="off"
               ></b-form-input>
            </b-form-group>

            <b-form-group label="Company Officer" label-for="select-new-officer">
               <template #label
                  >Company Officer
                  <b-icon
                     icon="question-circle"
                     v-b-tooltip.bottom="
                        'For (S/C)Corp, officers are identified by Form 1125-E. For partnerships and LLCs, officers are any members with officer-like authority.'
                     "
                  ></b-icon
               ></template>
               <b-form-select
                  id="select-edit-officer"
                  :options="[
                     {value: true, text: 'Yes'},
                     {value: false, text: 'No'},
                  ]"
                  v-model="editEmployee.officer"
               >
               </b-form-select>
            </b-form-group>

            <b-form-group label="Subsidiary Company Name" label-for="input-edit-subsidiary">
               <b-form-input
                  id="input-edit-subsidiary"
                  v-model="editEmployee.subsidiary"
                  list="datalist-subsidiaries"
                  autocomplete="off"
               ></b-form-input>
            </b-form-group>

            <label for="payroll" class="form-label fw-bold">Employee Taxable Wages</label>
            <div class="currency-input-wrapper">
               <span class="currency-symbol">$</span>
               <b-form-input
                  id="payroll"
                  v-model="editPayroll"
                  type="text"
                  @blur="formatEditCurrency"
                  @focus="unformatEditCurrency"
                  class="currency-input"
                  @keypress="onlyNumbers"
               />
            </div>
         </b-form>
      </b-modal>

      <DataFileImport
         ref="fileImport"
         section="employee"
         section-label="Employees"
         :columns="fileImportColumns"
         :label-file="false"
         :periodId="periodId"
         :skipped-record-warning="false"
      >
         <template #file-select-title>Upload your employee data</template>
         <template #file-instructions>
            Imported files must include <b>Full Name</b> and <b>U.S. State</b> columns. Additional
            columns for <b>Unique Employee ID</b>, <b>Title</b>, <b>Department</b>,
            <b>Subsidiary Company Name</b> and <b>Payroll</b> can also be provided.
         </template>
      </DataFileImport>

      <b-modal
         id="modal-confirm-delete"
         title="Delete Employees"
         centered
         ok-title="Delete"
         ok-variant="danger"
         @ok="confirmDeleteEmployees"
      >
         Are you sure you want to delete the <b>{{ selectedEmployeeIds.length }}</b> selected
         employees?
      </b-modal>

      <b-modal id="modal-cannot-delete" title="Cannot Delete Selected Employees" centered ok-only>
         <p>
            The following employees have related data and cannot be deleted by a staff user.
            Unselect these employees to proceed, or contact an administrator to delete these
            employees.
         </p>

         <ul>
            <li v-for="employee in invalidForDelete" :key="employee.id">{{ employee.fullname }}</li>
         </ul>
      </b-modal>

      <datalist id="datalist-titles">
         <option v-for="title in companyTitles" :key="title">
            {{ title }}
         </option>
      </datalist>

      <datalist id="datalist-departments">
         <option v-for="department in companyDepartments" :key="department">
            {{ department }}
         </option>
      </datalist>

      <datalist id="datalist-subsidiaries">
         <option v-for="subsidiary in companySubsidiaries" :key="subsidiary">
            {{ subsidiary }}
         </option>
      </datalist>
   </b-container>
</template>

<script>
import Vue from 'vue';
import {mapGetters} from 'vuex';
import {required} from 'vuelidate/lib/validators';
import {MAX_INTERNAL_FILE_SIZE} from '@/helpers/constants';
import {formatBytes, formatCurrency} from '@/helpers/utils';
import {Employee} from '@/store/models/employee';

import {clientUserSuggestions} from '@/mixins';
import StudyPeriodSelect from '@/components/forms/StudyPeriodSelect';
import DataFileImport from '../expenses/components/DataFileImport';

export default {
   components: {
      StudyPeriodSelect,
      DataFileImport,
   },

   mixins: [
      // Adds support for department/job title suggestions
      clientUserSuggestions,
   ],

   data() {
      return {
         periodId: null,
         newEmployee: new Employee(),
         editEmployee: new Employee(),
         payroll: null,
         editPayroll: null,
         permissions: {
            payroll: false,
            revenue: false,
         },
         fileImportColumns: [
            {key: 'fullname', label: 'Full Name', required: true},
            {key: 'location', label: 'U.S. State', required: true},
            {key: 'ident', label: 'Employee Id', required: false},
            {key: 'title', label: 'Title', required: false},
            {key: 'department', label: 'Department', required: false},
            {key: 'subsidiary', label: 'Subsidiary', required: false},
            {key: 'officer', label: 'Officer', required: false},
            {key: 'payroll', label: 'Payroll', required: false},
         ],
         uploads: {
            file: null,
            uploading: false,
         },
         selection: {},
         invalidForDelete: [],
         MAX_INTERNAL_FILE_SIZE,
      };
   },

   computed: {
      ...mapGetters({
         isRndig: 'isRndig',
         profile: 'profile',
         usStates: 'usStates',
         activeStudyId: 'companies/activeStudyId',
         studyPeriodMap: 'companies/studyPeriodMap',
         hasOpenStudy: 'companies/hasOpenStudy',
         studyPeriods: 'companies/studyPeriods',
         employees: 'legacyEmployees/employees',
         employeeMap: 'legacyEmployees/employeeMap',
         uploadCategories: 'uploads/externalUploadCategories',
         uploadProgress: 'uploads/progress',
      }),

      studyPeriod() {
         return this.studyPeriodMap[this.periodId];
      },

      // Employee table fields
      fields() {
         let fields = [
            {key: 'fullname', label: 'Name', sortable: true, stickyColumn: true},
            {key: 'location', label: 'U.S. State', sortable: true},
            {key: 'ident', label: 'Employee ID', sortable: true},
            {key: 'title', label: 'Job Title', sortable: true},
            {key: 'department', sortable: true},
            {
               key: 'officer',
               sortable: true,
               formatter: (x) => (x ? 'Yes' : 'No'),
               sortByFormatted: true,
            },
         ];

         // Only include the subsidiary col if at least one employee has one
         if (this.employees.some((employee) => employee.subsidiary)) {
            fields.push({key: 'subsidiary', sortable: true});
         }

         if (this.isRndig) {
            fields.push({
               key: 'payroll',
               label: 'Employee Taxable Wages',
               sortable: false,
               sortByFormatted: true,
               tdClass: 'cell-class',
               thClass: 'cell-class payroll-cell-class',
               formatter: (value) => (value != null ? formatCurrency(value / 100) : ''),
            });
         }
         return fields;
      },

      /** US States formatted for a b-form-select options attribute */
      stateOptions() {
         const stateOptions = Object.entries(this.usStates).map((entry) => {
            const [key, value] = entry;
            return {text: value, value: key};
         });
         return stateOptions;
      },

      /** Have any employees been selected for deletion? */
      anyEmployeesSelected() {
         return Object.values(this.selection).includes(true);
      },

      /** An array of employee ids selected for deletion */
      selectedEmployeeIds() {
         return Object.entries(this.selection)
            .filter((entry) => entry[1])
            .map((entry) => entry[0]);
      },

      /** Total size of selected file for upload */
      uploadSize() {
         return this.uploads.file?.size || 0;
      },
   },

   methods: {
      formatBytes,

      // Load employees for the selected year
      async loadEmployees() {
         const period = this.studyPeriodMap[this.periodId];
         this.$store.commit('companies/setActiveStudy', period.studyId);
         await this.blockingRequest('legacyEmployees/loadEmployees', {
            companyId: this.$companyId,
            periodId: this.periodId,
         });
      },

      resetNewEmployee() {
         this.newEmployee = new Employee();
         this.payroll = null;
      },

      formatCurrency() {
         if (this.payroll === '' || this.payroll === null) return;
         let number = parseFloat(this.payroll.toString().replace(/,/g, ''));
         if (!isNaN(number)) {
            this.payroll = number.toLocaleString('en-US', {
               minimumFractionDigits: 2,
               maximumFractionDigits: 2,
            });
         }
      },
      unformatCurrency() {
         if (this.payroll) {
            this.payroll = this.payroll.replace(/,/g, '');
         }
      },
      onlyNumbers(evt) {
         const char = String.fromCharCode(evt.which);
         if (!/[0-9.]/.test(char)) {
            evt.preventDefault();
         }
      },

      formatEditCurrency() {
         if (this.editPayroll === '' || this.editPayroll === null) return;
         let number = parseFloat(this.editPayroll.toString().replace(/,/g, ''));
         if (!isNaN(number)) {
            this.editPayroll = number.toLocaleString('en-US', {
               minimumFractionDigits: 2,
               maximumFractionDigits: 2,
            });
         }
      },
      unformatEditCurrency() {
         if (this.editPayroll) this.editPayroll = this.editPayroll.replace(/,/g, '');
      },

      // POST a new employee for the currently selected period
      async submitNewEmployee() {
         if (this.$v.newEmployee.$invalid) {
            return;
         }

         if (this.payroll !== null) {
            this.unformatCurrency();
            this.newEmployee.payroll = Math.round(parseFloat(this.payroll) * 100);
         }

         this.newEmployee.companyId = this.$companyId;
         this.newEmployee.periodId = this.periodId;
         try {
            this.newEmployee.startSave();
            await this.$store.dispatch('legacyEmployees/createEmployee', {
               employee: this.newEmployee,
            });
            this.loadSuggestions(true);
            this.resetNewEmployee();
            this.$refs.newEmployeeName.focus();
         } catch (err) {
            const msg = err.response.data.errors[0].detail;
            const errCode = err.response.data.errors[0].code;
            if (msg.includes('UniqueViolationError')) {
               this.$store.commit('showAlert', {
                  msg: 'An employee with that Employee ID already exists.',
                  seconds: 5,
               });
            } else if (![120, 121].includes(errCode)) {
               this.$store.commit('showAlert', {
                  response: err.response,
                  fallbackMsg: 'Submission failed',
                  seconds: 5,
               });
            }
         } finally {
            this.newEmployee.endSave();
         }
      },

      // Display the edit employee modal
      editEmployeeModal(employee) {
         this.editEmployee = employee.copy();
         this.editPayroll =
            this.editEmployee.payroll != null ? (this.editEmployee.payroll / 100).toFixed(2) : null;
         this.$bvModal.show('modal-edit-employee');
      },

      // Send an update employee request
      async submitEditEmployee() {
         if (this.$v.editEmployee.$invalid) {
            return;
         }

         this.unformatEditCurrency();
         this.editEmployee.payroll = Math.round(parseFloat(this.editPayroll) * 100);
         this.editEmployee.periodId = this.periodId;
         try {
            await this.blockingRequest('legacyEmployees/editEmployee', {
               employee: this.editEmployee,
            });
            this.loadSuggestions(true);
         } catch (err) {
            const msg = err.response.data.errors[0].detail;
            if (msg.includes('UniqueViolationError')) {
               this.$store.commit('showAlert', {
                  msg: 'An employee with that Employee ID already exists.',
                  seconds: 5,
               });
            } else {
               this.$store.commit('showAlert', {
                  response: err.response,
                  fallbackMsg: 'Submission failed',
                  seconds: 5,
               });
            }
         }

         this.editPayroll = null;
      },

      /** Set employee selection state to `value` for all employees */
      selectAll(value) {
         for (const id in this.selection) {
            this.selection[id] = value;
         }
      },

      async deleteEmployeesModal() {
         const responses = this.selectedEmployeeIds.map((employeeId) => {
            return this.$store.dispatch('legacyEmployees/deleteEmployee', {employeeId}).then(
               () => {
                  return {id: employeeId, canDelete: true};
               },
               () => {
                  return {id: employeeId, canDelete: false};
               }
            );
         });

         this.$emit('showSpinner', true);
         const results = await Promise.all(responses);
         this.$emit('showSpinner', false);

         // If any employees can't be deleted, reject the whole operation
         const cantDelete = results.filter((result) => !result.canDelete);
         if (cantDelete.length > 0) {
            this.invalidForDelete = cantDelete.map((result) => {
               return this.employeeMap[result.id];
            });
            this.$bvModal.show('modal-cannot-delete');
         } else {
            this.$bvModal.show('modal-confirm-delete');
         }
      },

      async confirmDeleteEmployees() {
         const responses = this.selectedEmployeeIds.map((employeeId) => {
            return this.$store.dispatch('legacyEmployees/deleteEmployee', {
               employeeId,
               force: true,
            });
         });
         await this.blockUntilAllSettled(responses);
      },

      startEmployeeImport() {
         this.$refs.fileImport.startNewImport();
      },

      // Send a request to import an employee CSV from an uploaded file
      async importEmployeeCsv(evt) {
         if (this.$v.uploads.$invalid) {
            return;
         }

         // Prevent the modal from closing immediately
         evt.preventDefault();

         try {
            this.uploads.uploading = true;
            await this.blockingRequest('legacyEmployees/importEmployeeCsv', {
               companyId: this.$companyId,
               periodId: this.periodId,
               file: this.uploads.file,
            });
         } catch (err) {
            const msg = err.response.data.errors[0].detail;
            const thisErrorCode = err.response.data.errors[0].code;
            const errCodes = this.$errorCodes();

            if (thisErrorCode === errCodes.CONFIRMATION_REQUIRED) {
               const force = await this.$bvModal.msgBoxConfirm(
                  `Importing employees will invalidate employee time data that has been submitted for ${this.studyPeriod.label}. Would you like to proceed?`,
                  {
                     centered: true,
                     title: 'Employee Time Already Submitted',
                  }
               );
               if (force) {
                  await this.blockingRequest('legacyEmployees/importEmployeeCsv', {
                     companyId: this.$companyId,
                     periodId: this.periodId,
                     file: this.uploads.file,
                     force: true,
                  });
               }
            } else if (
               [errCodes.FILE_VALIDATION_FAILED, errCodes.NO_ENCODING_FOUND].includes(thisErrorCode)
            ) {
               this.$store.commit('showAlert', {
                  msg: 'The file does not contain valid CSV data.',
                  seconds: 10,
               });
            } else if (msg.includes('CardinalityViolationError')) {
               this.$store.commit('showAlert', {
                  msg: 'The CSV file contains multiple employees with the same employee ID.',
                  seconds: 10,
               });
            } else if (msg.includes('NotNullViolationError')) {
               this.$store.commit('showAlert', {
                  msg: 'The CSV file is missing required data.',
                  seconds: 10,
               });
            } else if (msg.includes('InvalidTextRepresentationError')) {
               this.$store.commit('showAlert', {
                  msg: 'The CSV file contains an invalid location.',
                  seconds: 10,
               });
            } else {
               this.$store.commit('showAlert', {
                  response: err.response,
                  fallbackMsg: 'Submission failed',
                  seconds: 5,
               });
            }
         } finally {
            this.uploads.uploading = false;
            this.$bvModal.hide('modal-import-employees');
         }
      },
   },

   validations() {
      return {
         newEmployee: {
            fullname: {required},
            location: {required},
         },
         editEmployee: {
            fullname: {required},
            location: {required},
         },
         uploads: {
            file: {
               required,
               fileSize(file) {
                  if (file) {
                     return file.size <= MAX_INTERNAL_FILE_SIZE;
                  }
                  return false;
               },
            },
         },
      };
   },

   watch: {
      employees(employees) {
         const newIds = employees.map((employee) => employee.id);
         // Clear unused selections
         Object.keys(this.selection).forEach((id) => {
            if (!newIds.includes(id)) {
               Vue.delete(this.selection, id);
            }
         });

         // Init new selections
         newIds.forEach((id) => {
            if (!(id in this.selection)) {
               Vue.set(this.selection, id, false);
            }
         });
      },

      periodId() {
         this.resetNewEmployee();
         this.$nextTick(() => {
            this.$refs.newEmployeeName.focus();
         });
      },
   },

   async created() {
      const requests = [
         this.$store.dispatch('companies/loadCompany', {
            companyId: this.$companyId,
         }),
         this.loadSuggestions(),
         this.$store.dispatch('employeeUploads/loadUploadCategory', {
            companyId: this.$companyId,
         }),
      ];

      const permissionFlags = await this.$store.dispatch('users/fetchUserPermissionFlags', {
         userId: this.profile.id,
      });
      this.permissions = permissionFlags;

      await this.loadPage(requests);

      // Initialize selection data
      this.employees.forEach((employee) => {
         Vue.set(this.selection, employee.id, false);
      });

      this.resetNewEmployee();

      // If the company has no active study, redirect to company detail
      if (!this.hasOpenStudy) {
         this.$router.push({name: 'company-detail', params: {id: this.$companyId}});
      }

      // Check the query params for an initial year to select
      if (this.$route.query.periodId) {
         this.periodId = Number(this.$route.query.periodId);
      }
   },
};
</script>
<style scoped>
.cell-class {
   font-size: 12px;
}

.payroll-cell-class {
   font-size: 12px;
   min-width: 130px !important;
}

.currency-input-wrapper {
   position: relative;
   display: inline-block;
   width: 100%;
}

.currency-symbol {
   position: absolute;
   top: 50%;
   left: 10px;
   transform: translateY(-50%);
   color: #6c757d; /* grey */
   font-weight: 500;
}
.currency-input {
   padding-left: 25px;
}
</style>
